import { Link, useLocation } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { selectOrgCustomization } from "src/store/orgCustomizations/reducer";
import {
    findActiveMenuMatch,
    getActiveMenuIndexKey,
    isMenuIndexActive,
} from "../../menuPathUtils";

const LoadingComponent = () => {
    return (
        <>
            <li className="afc-dropdown" style={{ width: "80%" }}>
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown">
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown">
                <div className="loading-placeholder menu-item w-50" />
            </li>
            <li className="afc-dropdown" style={{ width: "70%" }}>
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown">
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown" style={{ width: "80%" }}>
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown" style={{ width: "80%" }}>
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown">
                <div className="loading-placeholder menu-item" />
            </li>
            <li className="afc-dropdown">
                <div className="loading-placeholder menu-item w-50" />
            </li>
        </>
    );
};

const VerticalMenuContent = (props) => {
    const { menu, t, modules, organization, orgCustomization } = props;

    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    // Manual expand for parents without a matching URL (toggle only).
    const [manualOpenKey, setManualOpenKey] = useState(null);

    const activeMenuKey = useMemo(() => {
        const match = findActiveMenuMatch(menu || [], location.pathname);
        return getActiveMenuIndexKey(match);
    }, [menu, location.pathname]);

    useEffect(() => {
        // When the URL changes, prefer URL-derived open state.
        setManualOpenKey(null);
    }, [location.pathname]);

    useEffect(() => {
        setIsLoading(!(!modules.isLoading && modules.featuresLoadingStatus === "loaded"));
    }, [modules.isLoading, modules.featuresLoadingStatus]);

    const isOpen = (...parts) => {
        if (isMenuIndexActive(activeMenuKey, ...parts)) {
            return true;
        }

        if (!manualOpenKey) {
            return false;
        }

        return isMenuIndexActive(manualOpenKey, ...parts);
    };

    const toggleManualOpen = (key, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setManualOpenKey((current) => (current === key ? null : key));
    };

    const renderTitle = (item) => {
        return typeof item.titleKey === "function"
            ? item.titleKey({
                  t,
                  organization,
                  orgCustomization,
                  menuItem: item,
              })
            : t(item.titleKey);
    };

    return (
        <ul className="metismenu list-unstyled" id="side-menu">
            {(() => {
                if (isLoading) {
                    return <LoadingComponent />;
                }

                return (menu || []).map((menuItem, menuItemIndex) => {
                    const subMenuItems = menuItem.items || [];
                    const hasSubMenu = subMenuItems.length > 0;
                    const topKey = `${menuItemIndex}`;
                    const topActive = isOpen(menuItemIndex);
                    const topExactActive = activeMenuKey === topKey;

                    return (
                        <li
                            key={menuItemIndex}
                            className={`afc-dropdown ${
                                topActive ? "active mm-active" : ""
                            }`}
                        >
                            {menuItem.linkType === "react-router" ? (
                                <>
                                    {menuItem?.path ? (
                                        <Link
                                            to={menuItem.path}
                                            className={`${hasSubMenu ? "has-arrow" : ""} waves-effect${
                                                topExactActive ? " active" : ""
                                            }`.trim()}
                                        >
                                            {typeof menuItem.icon === "function" ? (
                                                <menuItem.icon />
                                            ) : (
                                                <i className={menuItem.icon}></i>
                                            )}
                                            <span className="ms-1">{renderTitle(menuItem)}</span>
                                        </Link>
                                    ) : (
                                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                        <a
                                            href="#"
                                            className={`${hasSubMenu ? "has-arrow" : ""} waves-effect`.trim()}
                                            onClick={(event) => toggleManualOpen(topKey, event)}
                                        >
                                            {typeof menuItem.icon === "function" ? (
                                                <menuItem.icon />
                                            ) : (
                                                <i className={menuItem.icon}></i>
                                            )}
                                            <span className="ms-1">{renderTitle(menuItem)}</span>
                                        </a>
                                    )}
                                </>
                            ) : (
                                <a
                                    href={menuItem.path}
                                    className="waves-effect"
                                    target="_new"
                                >
                                    {typeof menuItem.icon === "function" ? (
                                        <menuItem.icon />
                                    ) : (
                                        <i className={menuItem.icon}></i>
                                    )}
                                    <span className="ms-1">{renderTitle(menuItem)}</span>
                                </a>
                            )}

                            {hasSubMenu ? (
                                <ul className="sub-menu">
                                    {subMenuItems.map((subMenuItem, subMenuItemIndex) => {
                                        const nestedItems = subMenuItem.items || [];
                                        const hasNested = nestedItems.length > 0;
                                        const subKey = `${menuItemIndex}--${subMenuItemIndex}`;
                                        const subActive = isOpen(menuItemIndex, subMenuItemIndex);
                                        const subExactActive = activeMenuKey === subKey;

                                        return (
                                            <li
                                                key={subMenuItemIndex}
                                                className={`${hasNested ? "afc-dropdown" : ""} ${
                                                    subActive ? "active mm-active" : ""
                                                }`.trim()}
                                            >
                                                {subMenuItem.linkType === "react-router" ? (
                                                    <>
                                                        {subMenuItem?.path ? (
                                                            <Link
                                                                to={subMenuItem.path}
                                                                className={`${
                                                                    hasNested ? "has-arrow" : ""
                                                                } waves-effect${
                                                                    subExactActive ? " active" : ""
                                                                }`.trim()}
                                                            >
                                                                <span className="ms-1">
                                                                    {renderTitle(subMenuItem)}
                                                                </span>
                                                            </Link>
                                                        ) : (
                                                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                                            <a
                                                                href="#"
                                                                className={`${
                                                                    hasNested ? "has-arrow" : ""
                                                                } waves-effect`.trim()}
                                                                onClick={(event) =>
                                                                    toggleManualOpen(subKey, event)
                                                                }
                                                            >
                                                                <span className="ms-1">
                                                                    {renderTitle(subMenuItem)}
                                                                </span>
                                                            </a>
                                                        )}
                                                    </>
                                                ) : (
                                                    <a href={subMenuItem.path} target="_new">
                                                        {renderTitle(subMenuItem)}
                                                    </a>
                                                )}

                                                {hasNested ? (
                                                    <ul className="sub-menu">
                                                        {nestedItems.map(
                                                            (nestedItem, nestedIndex) => {
                                                                const nestedActive = isOpen(
                                                                    menuItemIndex,
                                                                    subMenuItemIndex,
                                                                    nestedIndex
                                                                );

                                                                return (
                                                                    <li
                                                                        key={nestedIndex}
                                                                        className={
                                                                            nestedActive
                                                                                ? "active mm-active"
                                                                                : ""
                                                                        }
                                                                    >
                                                                        {nestedItem.linkType ===
                                                                        "react-router" ? (
                                                                            <Link
                                                                                to={nestedItem.path}
                                                                                className={
                                                                                    nestedActive
                                                                                        ? "active"
                                                                                        : ""
                                                                                }
                                                                            >
                                                                                {renderTitle(
                                                                                    nestedItem
                                                                                )}
                                                                            </Link>
                                                                        ) : (
                                                                            <a
                                                                                href={nestedItem.path}
                                                                                target="_new"
                                                                            >
                                                                                {renderTitle(
                                                                                    nestedItem
                                                                                )}
                                                                            </a>
                                                                        )}
                                                                    </li>
                                                                );
                                                            }
                                                        )}
                                                    </ul>
                                                ) : null}
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : null}
                        </li>
                    );
                });
            })()}
        </ul>
    );
};

const mapStatetoProps = (state) => {
    const { Modules: modules, Organization: organization } = state;
    return {
        modules,
        organization,
        orgCustomization: selectOrgCustomization(state, organization?.id),
    };
};

export default withNamespaces()(
    connect(mapStatetoProps, {})(VerticalMenuContent)
);
